import { useEffect, useState, useCallback } from 'react';
import { makeVar } from '@apollo/client';

import {
  toCreateClientInput,
  toUpdateClientInput,
} from 'sharedHelpers/toClientInput';
import {
  toCreateContactInput,
  toUpdateContactInput,
} from 'sharedHelpers/toContactInput';
import {
  useCreateClientMutation,
  useUpdateClientMutation,
  useCreateContactMutation,
  useUpdateContactMutation,
  GetClientDetailsQuery,
  GetClientAssignedStaffDocument,
  GetUserQuery,
} from 'generated/graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { allClientsListVar } from '../graphql/apolloCache';
import type { ClientFormFields, ContactFormFields } from '../types/formTypes';
import saveClientRefetch from '../graphql/queries/saveClientRefetch';
import getClientDetailsQuery from '../graphql/queries/getClientDetails';
import getUser from '../graphql/queries/getUser';

export type UseSaveClientData = [
  (clientData: ClientFormFields & { needsPing?: boolean }) => void,
  {
    loading: boolean;
    clientError: boolean;
    contactError: boolean;
    complete: boolean;
  }
];

export const newClientId = makeVar('');
type ClientListItem = ReturnType<typeof allClientsListVar>[number];

const addClientToClientListVar = (client: ClientListItem): void => {
  const existingClients = allClientsListVar();

  if (
    existingClients.some((existingClient) => existingClient.id === client.id)
  ) {
    return;
  }

  allClientsListVar([...existingClients, client]);
};

function useSaveClient(): UseSaveClientData {
  const [contacts, setContacts] = useState<ContactFormFields[]>([]);
  const [loading, setLoading] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [contactError, setContactError] = useState(false);
  const [complete, setComplete] = useState(false);
  const [saving, setSaving] = useState(false);
  const { filterIncludeEventStaff } = useFlags();

  const [
    createClientMutation,
    {
      data: createClientData,
      loading: createClientLoading,
      error: createClientError,
    },
  ] = useCreateClientMutation();

  const [
    createContactMutation,
    {
      data: createContactData,
      loading: createContactLoading,
      error: createContactError,
    },
  ] = useCreateContactMutation();

  const [
    updateClientMutation,
    { loading: updateClientLoading, error: updateClientError },
  ] = useUpdateClientMutation();

  const [
    updateContactMutation,
    { loading: updateContactLoading, error: updateContactError },
  ] = useUpdateContactMutation();

  const save = useCallback(
    (clientData: ClientFormFields & { needsPing?: boolean }) => {
      setSaving(true);
      setContacts(clientData.contacts);
      if (clientData.id) {
        newClientId(clientData.id || '');
        updateClientMutation({
          variables: { input: toUpdateClientInput(clientData) },
          refetchQueries: [
            { query: saveClientRefetch },
            {
              query: GetClientAssignedStaffDocument,
              variables: { id: clientData.id },
            },
          ],
        });
      } else {
        createClientMutation({
          variables: { input: toCreateClientInput(clientData) },
          update(cache, { data }) {
            const newClient = data?.createClient?.client;
            if (!newClient) return;

            newClientId(newClient.id);

            const newClientNode = {
              ...newClient,
              ...(filterIncludeEventStaff ? { assignedStaff: [] } : {}),
            };

            addClientToClientListVar(
              (newClientNode as unknown) as ClientListItem
            );

            // Update old getUser cache, if present.
            const existingUser = cache.readQuery<GetUserQuery>({
              query: getUser,
              variables: {
                filterIncludeEventStaff: filterIncludeEventStaff || false,
                includeClients: true,
              },
            });

            if (
              // eslint-disable-next-line no-underscore-dangle
              existingUser?.getUser?.__typename === 'StaffUser' &&
              existingUser.getUser.clients
            ) {
              const existingUserEdges =
                existingUser.getUser.clients.edges || [];

              cache.writeQuery<GetUserQuery>({
                query: getUser,
                variables: {
                  filterIncludeEventStaff: filterIncludeEventStaff || false,
                  includeClients: true,
                },
                data: {
                  getUser: {
                    ...existingUser.getUser,
                    clients: {
                      ...existingUser.getUser.clients,
                      edges: [
                        ...existingUserEdges,
                        // eslint-disable-next-line no-underscore-dangle
                        { node: newClientNode, __typename: 'ClientEdge' },
                      ],
                    },
                  },
                },
              });
            }
          },
        });
      }
    },
    [
      createClientMutation,
      createContactMutation,
      updateClientMutation,
      updateContactMutation,
      filterIncludeEventStaff,
      newClientId(),
    ]
  );

  // Just for update
  useEffect(() => {
    if (updateClientError) {
      setClientError(true);
      setLoading(false);
    }
    if (updateClientLoading) {
      setLoading(true);
    }
    if (
      newClientId() &&
      !updateClientLoading &&
      !createContactLoading &&
      !updateContactLoading &&
      !createContactData &&
      !!contacts.length
    ) {
      contacts.forEach((contact) => {
        if (contact.name) {
          if (contact.id) {
            updateContactMutation({
              variables: { input: toUpdateContactInput(contact) },
            });
          } else {
            createContactMutation({
              variables: { input: toCreateContactInput(contact) },
              update: (cache, { data }) => {
                const newContactFromResponse = data?.createContact.contact;
                if (!newContactFromResponse) {
                  return;
                }
                const clientData = cache.readQuery<GetClientDetailsQuery>({
                  query: getClientDetailsQuery,
                  variables: { id: newClientId() },
                });
                if (!clientData?.getClient) {
                  return;
                }
                const client = clientData.getClient;
                const currentContacts = client.contacts.edges || [];

                const newContacts = [
                  ...currentContacts,
                  { node: newContactFromResponse },
                ];
                cache.writeQuery<GetClientDetailsQuery>({
                  query: getClientDetailsQuery,
                  variables: { id: newClientId() },
                  data: {
                    getClient: {
                      ...client,
                      contacts: { ...client.contacts, edges: newContacts },
                    },
                  },
                });
              },
            });
          }
        }
      });
    }
  }, [
    createContactLoading,
    updateContactLoading,
    updateClientError,
    updateClientLoading,
  ]);

  // Just for Creation
  useEffect(() => {
    if (createClientError || updateClientError) {
      setClientError(true);
      setLoading(false);
    }
    if (createClientLoading || updateClientLoading) {
      setLoading(true);
    }
    if (
      createClientData &&
      (createClientData?.createClient?.client?.id || newClientId()) &&
      !createContactLoading &&
      !updateContactLoading &&
      !createContactData
    ) {
      if (!newClientId()) {
        newClientId(createClientData.createClient.client.id);
      }
      if (contacts.length) {
        contacts.forEach((contact) => {
          if (contact.name) {
            if (contact.id) {
              updateContactMutation({
                variables: { input: toUpdateContactInput(contact) },
              });
            } else {
              createContactMutation({
                variables: { input: toCreateContactInput(contact) },
                update: (cache, { data }) => {
                  const newContactFromResponse = data?.createContact.contact;
                  if (!newContactFromResponse) {
                    return;
                  }
                  const clientData = cache.readQuery<GetClientDetailsQuery>({
                    query: getClientDetailsQuery,
                    variables: { id: newClientId() },
                  });
                  if (!clientData?.getClient) {
                    return;
                  }
                  const client = clientData.getClient;
                  const currentContacts = client.contacts.edges || [];

                  const newContacts = [
                    ...currentContacts,
                    { node: newContactFromResponse },
                  ];
                  cache.writeQuery<GetClientDetailsQuery>({
                    query: getClientDetailsQuery,
                    variables: { id: newClientId() },
                    data: {
                      getClient: {
                        ...client,
                        contacts: { ...client.contacts, edges: newContacts },
                      },
                    },
                  });
                },
              });
            }
          }
        });
      } else if (saving) {
        setLoading(false);
        setComplete(true);
      }
    }
  }, [
    createClientError,
    createClientLoading,
    createClientData,
    updateClientError,
    updateClientLoading,
  ]);

  useEffect(() => {
    if (createContactError || updateContactError) {
      setContactError(true);
      setLoading(false);
    }
    if (
      saving &&
      !createClientLoading &&
      !createContactLoading &&
      !updateContactLoading &&
      !updateClientLoading
    ) {
      setLoading(false);
      setComplete(true);
      setSaving(false);
    }
  }, [
    createContactError,
    createContactLoading,
    createClientData,
    createClientLoading,
    updateContactLoading,
    updateClientLoading,
    updateContactError,
  ]);

  return [
    save,
    {
      loading,
      clientError,
      contactError,
      complete,
    },
  ];
}

export default useSaveClient;
